"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Activity, ShieldCheck, Stethoscope } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const careWings = [{
  title: "Brain & Cranial Care",
  description: "Advanced microsurgical and minimally invasive techniques for complex conditions, prioritizing cognitive preservation and rapid recovery.",
  icon: Activity,
  link: "/YourJourneyPage"
}, {
  title: "Spine & Mobility",
  description: "Motion-preserving surgery designed to restore function and relieve pain through state-of-the-art technology and smaller incisions.",
  icon: ShieldCheck,
  link: "/YourJourneyPage"
}, {
  title: "Peripheral Nerves",
  description: "Expert diagnosis and surgical repair of nerve injuries and entrapments, restoring sensation and motor control to the extremities.",
  icon: Stethoscope,
  link: "/YourJourneyPage"
}];
export default function SpecializedCareWings() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="135982" id="pathways-to-healing" className="relative overflow-hidden bg-background py-24 sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-30">
        <div className="h-[40rem] w-[40rem] rounded-full bg-primary/5 blur-3xl" />
        <div className="h-[30rem] w-[30rem] rounded-full bg-secondary/10 blur-3xl" />
      </div>

      <div ref={ref} className={`container relative mx-auto px-4 transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
        <div className="mx-auto max-w-2xl text-center">
          <Badge variant="secondary" className="mb-6 rounded-full px-4 py-1.5 text-sm font-medium tracking-tight text-secondary-foreground shadow-sm">
            PRECISION NEUROSURGERY
          </Badge>
          <h2 className="font-sans text-4xl font-bold tracking-tighter text-primary sm:text-5xl">
            Pathways to Healing
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground font-sans">
            Integrating surgical excellence with the compassion of family to manage complex disorders of the brain, spine, and peripheral nerves.
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-8 sm:mt-20 md:grid-cols-3">
          {careWings.map((wing, i) => {
          const Icon = wing.icon;
          return <div data-index={i} key={i} className="h-full">
                <Card className="group flex h-full flex-col rounded-xl border-border bg-card/60 shadow-sm backdrop-blur-xl transition-all duration-600 hover:-translate-y-3 hover:bg-card/90 hover:shadow-xl hover:shadow-primary/5">
                  <CardHeader className="pb-4">
                    <div className="mb-4 inline-flex size-14 items-center justify-center rounded-xl bg-secondary/10 text-secondary transition-all duration-500 group-hover:bg-secondary group-hover:text-secondary-foreground group-hover:scale-105">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="text-xl font-bold tracking-tight text-primary font-sans">
                      {wing.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col justify-between">
                    <CardDescription className="text-base leading-relaxed text-muted-foreground font-sans">
                      {wing.description}
                    </CardDescription>
                    <div className="mt-8">
                      <Link to={wing.link} className="inline-flex items-center text-sm font-semibold text-secondary transition-colors hover:text-secondary/80">
                        Explore Treatments
                        <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-2" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}